import { Plus, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import {
  ALL_EVENTS,
  customSubscriptions,
  familyWildcard,
  groupByFamily,
  normaliseSubscriptions,
  subscriptionIssue,
} from '../schemas/webhook-rules';

export interface EventsPickerProps {
  known: readonly string[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  invalid?: boolean;
}

function Option({
  id,
  label,
  hint,
  checked,
  disabled,
  onToggle,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onToggle(next === true)}
      />
      <Label htmlFor={id} className="font-normal">
        <span>{label}</span>
        {hint ? <code className="text-muted-foreground text-xs">{hint}</code> : null}
      </Label>
    </div>
  );
}

/**
 * Subscriptions as a tree: everything, a whole family (`order.*`) or single events, plus typed
 * patterns for anything the tree does not list. A broader choice covers (and disables) the
 * narrower ones under it.
 */
export function EventsPicker({ known, value, onChange, invalid }: EventsPickerProps) {
  const { t } = useTranslation('webhooks');
  const baseId = useId();
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const all = value.includes(ALL_EVENTS);
  const set = (next: string[]) => onChange(normaliseSubscriptions(next));
  const custom = customSubscriptions(value, known);

  const addDraft = () => {
    const pattern = draft.trim();
    if (!pattern) return;
    const issue = subscriptionIssue(pattern, known);
    if (issue) {
      setDraftError(t(`events.issue.${issue}`));
      return;
    }
    set([...value, pattern]);
    setDraft('');
    setDraftError(null);
  };

  return (
    <div
      className={cn('flex flex-col gap-3 rounded-md border p-3', invalid && 'border-destructive')}
      role="group"
      aria-label={t('events.label')}
    >
      <Option
        id={`${baseId}-all`}
        label={t('events.all')}
        hint={ALL_EVENTS}
        checked={all}
        onToggle={(checked) => set(checked ? [ALL_EVENTS] : [])}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {groupByFamily(known).map(([family, events]) => {
          const wildcard = familyWildcard(family);
          const familyChecked = all || value.includes(wildcard);
          return (
            <fieldset key={family} className="flex flex-col gap-1.5">
              <legend className="sr-only">{family}</legend>
              <Option
                id={`${baseId}-${family}`}
                label={t('events.family', { family })}
                hint={wildcard}
                checked={familyChecked}
                disabled={all}
                onToggle={(checked) =>
                  set(checked ? [...value, wildcard] : value.filter((p) => p !== wildcard))
                }
              />
              <div className="flex flex-col gap-1.5 pl-6">
                {events.map((event) => (
                  <Option
                    key={event}
                    id={`${baseId}-${event}`}
                    label={event}
                    checked={familyChecked || value.includes(event)}
                    disabled={familyChecked}
                    onToggle={(checked) =>
                      set(checked ? [...value, event] : value.filter((p) => p !== event))
                    }
                  />
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
      {custom.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('events.custom')}>
          {custom.map((pattern) => (
            <li key={pattern}>
              <Badge variant="secondary" className="gap-1 font-mono">
                {pattern}
                <button
                  type="button"
                  className="hover:text-foreground"
                  aria-label={t('events.remove', { pattern })}
                  onClick={() => set(value.filter((p) => p !== pattern))}
                >
                  <X className="size-3" aria-hidden />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-col gap-1">
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDraft();
              }
            }}
            placeholder="order.*"
            aria-label={t('events.pattern')}
            aria-invalid={Boolean(draftError)}
            className="font-mono text-[13px]"
            disabled={all}
          />
          <Button type="button" variant="outline" onClick={addDraft} disabled={all}>
            <Plus aria-hidden />
            {t('events.add')}
          </Button>
        </div>
        {draftError ? (
          <p className="text-destructive text-xs" role="alert">
            {draftError}
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">{t('events.patternHint')}</p>
        )}
      </div>
    </div>
  );
}
